import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** Ba màn của máy trạm, theo đúng thứ tự vòng xoay. */
export type StationScreen = 'orders' | 'fleet' | 'work'

/** Nhịp xoay: 30 giây mỗi màn. */
export const ROTATE_MS = 30_000

/**
 * Bấm nút chuyển màn xong thì vòng xoay định kỳ đứng yên chừng này, hết mới
 * bắt đầu tính lượt 30 giây như thường (FD-393, khầy Long chốt 15 giây).
 */
export const MANUAL_HOLD_MS = 15_000

interface Options {
    /**
     * Màn công việc có nội dung để hiện không. Rỗng thì không tới lượt trong
     * vòng xoay, và nếu đang hiển thị thì bị trả về màn đơn — hiện bảng trống
     * còn tệ hơn.
     */
    workHasContent: boolean
    /**
     * Màn định vị xe có dùng được không (tài khoản có quyền xem xe và màn đủ
     * rộng). Không thì vòng xoay bỏ qua nó như với màn công việc rỗng.
     */
    fleetHasContent: boolean
    /**
     * Tài khoản xem được bảng công việc. Có thì nút "Công việc" vẫn bấm được
     * khi bảng rỗng: người bấm chủ động muốn xem, bấm mà không có gì xảy ra
     * còn khó hiểu hơn thấy "không còn lịch". Vòng xoay tự động vẫn bỏ qua
     * bảng rỗng như cũ.
     */
    canPickWork?: boolean
    /** Tắt hẳn vòng xoay (dùng cho test hoặc khi chỉ có một màn dùng được). */
    enabled?: boolean
}

interface Result {
    screen: StationScreen
    /** Các màn có nút chuyển, theo thứ tự vòng xoay. */
    selectable: StationScreen[]
    /**
     * Người dùng bấm nút chuyển màn: sang ngay, rồi giữ vòng xoay định kỳ
     * {@link MANUAL_HOLD_MS} trước khi tính lượt thường. Màn không có nút thì
     * bỏ qua.
     */
    selectScreen: (target: StationScreen) => void
    /** Gọi khi bên đơn có biến động (đơn mới, đơn đổi trạng thái được đọc loa). */
    signalOrders: () => void
    /**
     * Gọi khi bên công việc có biến động (giao việc mới, sắp tới giờ họp).
     *
     * Người gọi chịu trách nhiệm chỉ gọi khi bảng THẬT SỰ có thứ để xem — phía
     * SSE kiểm tra danh sách vừa nạp lại có chứa hoạt động đó không. Nếu lỡ gọi
     * lúc bảng rỗng thì hook tự trả về màn đơn ngay lần render kế tiếp.
     */
    signalWork: () => void
}

/**
 * Điều khiển ba màn của máy trạm.
 *
 * Luật:
 *  1. Cứ 30 giây đổi sang màn kế tiếp trong vòng: đơn → định vị → công việc.
 *  2. Đang ở màn nào mà màn ĐƠN hoặc màn CÔNG VIỆC có biến động thì nhảy sang
 *     ngay, không đợi hết 30 giây — đây là lý do tồn tại của cả cơ chế: người ở
 *     kho nghe loa xong ngẩng lên là thấy đúng thứ vừa được đọc.
 *  3. Màn ĐỊNH VỊ XE không có cú nhảy nào: xe chạy liên tục nên "biến động" là
 *     trạng thái thường trực, cho nó nhảy thì hai màn kia không bao giờ tới
 *     lượt. Nó chỉ đến khi đủ 30 giây (khầy Long chốt).
 *  4. Nhảy vì biến động thì đồng hồ tính lại từ đầu. Không đặt lại thì cú nhảy ở
 *     giây thứ 29 chỉ hiện màn mới đúng một giây rồi lại đổi.
 *  5. Màn không dùng được (công việc rỗng, hoặc không có quyền xem xe) thì
 *     không tới lượt, và đang hiển thị mà mất nội dung thì trả ngay về màn đơn.
 *  6. Bấm nút chuyển màn (FD-393) thì sang ngay, và vòng xoay ĐỊNH KỲ đứng yên
 *     15 giây rồi mới bắt đầu tính lượt 30 giây. Bấm lại (kể cả đúng màn đang
 *     xem) là tính lại từ lần bấm cuối. Cú nhảy vì biến động ở luật 2 vẫn chạy
 *     trong lúc giữ và kết thúc lượt giữ. Bảng công việc rỗng mở bằng nút được
 *     xem hết 15 giây, sau đó luật 5 trả về màn đơn.
 *
 * CHƯA có chống nhảy loạn (giờ cao điểm đơn về liên tục có thể làm các màn giật
 * qua lại) — khầy Long đã chốt để đúng logic trước, xử lý sau nếu thấy phiền.
 */
export function useStationScreenRotation({
    workHasContent,
    fleetHasContent,
    canPickWork = false,
    enabled = true,
}: Options): Result {
    const [screen, setScreen] = useState<StationScreen>('orders')
    // Lượt hiện tại do người bấm nút mở ra thì là số thứ tự lần bấm, còn lượt
    // tự động là null. Là số chứ không phải cờ: bấm lại đúng màn đang giữ phải
    // đổi được deps để đồng hồ dựng lại từ đầu.
    const [manualPick, setManualPick] = useState<number | null>(null)

    // Giữ trong ref để hai hàm signal ổn định qua các lần render — chúng được
    // truyền vào listener SSE, đổi định danh là đăng ký lại kết nối.
    const screenRef = useRef(screen)
    screenRef.current = screen

    // Vòng xoay chỉ gồm các màn đang dùng được. Màn đơn luôn có mặt: kể cả khi
    // không có đơn nào thì nó vẫn là màn mặc định để đứng.
    const cycle = useMemo(() => {
        const list: StationScreen[] = ['orders']
        if (fleetHasContent) list.push('fleet')
        if (workHasContent) list.push('work')
        return list
    }, [fleetHasContent, workHasContent])

    const selectable = useMemo(() => {
        const list: StationScreen[] = ['orders']
        if (fleetHasContent) list.push('fleet')
        if (workHasContent || canPickWork) list.push('work')
        return list
    }, [fleetHasContent, workHasContent, canPickWork])

    const selectableRef = useRef(selectable)
    selectableRef.current = selectable

    const isHeld = manualPick !== null
    // Màn đang hiện hợp lệ không: trong vòng xoay, hoặc đang được giữ bằng nút
    // mà vẫn còn nút (bảng công việc rỗng mở bằng tay).
    const screenAllowed =
        cycle.includes(screen) || (isHeld && selectable.includes(screen))

    // Lưới an toàn DUY NHẤT cho màn mất nội dung: đang hiển thị mà rơi khỏi
    // vòng xoay thì trả về màn đơn.
    //
    // Cố tình không chặn thêm ở `signalWork`. Chặn ở đó phải đọc `workHasContent`
    // của lần render GẦN NHẤT, mà lúc sự kiện tới thì dữ liệu mới có thể chưa
    // kịp render — đúng cảnh việc đầu ca, bảng vừa từ rỗng thành có việc, cú
    // nhảy lại bị nuốt vì cờ còn là "rỗng". Để một luật ở đây, chạy trên dữ
    // liệu đã render thật, thay vì hai luật cùng đoán.
    useEffect(() => {
        if (screenAllowed) return
        setScreen('orders')
        setManualPick(null)
    }, [screenAllowed])

    // Đồng hồ của lượt hiện tại. `screen` và `manualPick` nằm trong deps nên MỌI
    // cú đổi màn — hết lượt, nhảy vì biến động hay bấm nút — đều dựng lại hẹn
    // giờ từ đầu; không cần giữ thêm mốc thời gian nào, và cũng không được giữ:
    // một biến chỉ để đó mà không điều khiển gì là bẫy cho lần sửa sau.
    useEffect(() => {
        if (!enabled) return
        const inCycle = cycle.includes(screen)
        // Chỉ có một màn dùng được thì không hẹn giờ: đặt hẹn rồi lần nào cũng
        // không đổi được gì chỉ tổ đánh thức tablet vô ích. Ngoại lệ là đang
        // giữ một màn ngoài vòng xoay: hết giữ vẫn phải trả về màn đơn.
        if (inCycle && cycle.length < 2) return

        // Lượt mở bằng nút: đứng yên 15 giây rồi mới tính lượt 30 giây. Màn
        // ngoài vòng xoay thì không có lượt 30 giây nào để tính — hết 15 giây
        // là về.
        const delay =
            manualPick === null
                ? ROTATE_MS
                : inCycle
                  ? MANUAL_HOLD_MS + ROTATE_MS
                  : MANUAL_HOLD_MS

        const timer = setTimeout(() => {
            setManualPick(null)
            setScreen((prev) => {
                const at = cycle.indexOf(prev)
                // Màn hiện tại không nằm trong vòng xoay (indexOf = −1): về màn
                // đơn, đúng thứ hiệu ứng ở trên cũng sắp làm.
                if (at < 0) return 'orders'
                return cycle[(at + 1) % cycle.length] ?? 'orders'
            })
        }, delay)
        return () => clearTimeout(timer)
    }, [enabled, cycle, screen, manualPick])

    const jumpTo = useCallback((target: StationScreen) => {
        // Đang đứng sẵn ở đó thì không làm gì: mỗi đơn mới về sẽ gia hạn vô hạn
        // lượt hiện tại và màn kia không bao giờ tới lượt. Ghi vào ref ngay để
        // hai sự kiện trong cùng một nhịp không đếm thành hai lượt.
        if (screenRef.current === target) return
        screenRef.current = target
        setScreen(target)
        setManualPick(null)
    }, [])

    const selectScreen = useCallback((target: StationScreen) => {
        if (!selectableRef.current.includes(target)) return
        screenRef.current = target
        setScreen(target)
        setManualPick((prev) => (prev ?? 0) + 1)
    }, [])

    const signalOrders = useCallback(() => jumpTo('orders'), [jumpTo])
    const signalWork = useCallback(() => jumpTo('work'), [jumpTo])

    return { screen, selectable, selectScreen, signalOrders, signalWork }
}
